<template>
  <div class="modal-overlay" @click.self="onCancel">
    <div class="modal-panel">
      <div class="modal-header">
        <h3 class="modal-title">
          <span class="modal-title-badge">GPR</span>
          {{ isEditing ? 'Modifier la ligne GPR' : 'Nouvelle ligne GPR' }}
        </h3>
        <button type="button" class="modal-close-btn" @click="onCancel" title="Fermer">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Préco travaux -->
        <div class="modal-section">
          <div class="form-row-compact">
            <div class="form-group">
              <label class="label-compact">Préco travaux</label>
              <select
                v-model="draft.precoTravauxGPR"
                class="input-field"
              >
                <option value="">-- Sélectionner --</option>
                <option
                  v-for="[value, label] in sortedPrecoTravauxOptions"
                  :key="value"
                  :value="value"
                >{{ label }}</option>
              </select>
            </div>

            <div class="form-group">
              <label class="label-compact">Durée prév. (jours)</label>
              <input
                type="number"
                v-model.number="draft.dureePrevTravauxGPR"
                placeholder="Nb jours"
                min="0"
                class="input-field"
              />
            </div>

            <div class="form-group">
              <label class="label-compact">Validé EDF</label>
              <select
                v-model="draft.travauxGPRValidesEDF"
                class="input-field"
              >
                <option value="">-- Sélectionner --</option>
                <option value="OUI">OUI</option>
                <option value="NON">NON</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Coûts -->
        <div class="modal-section">
          <div class="section-title">Coûts</div>
          <div class="form-row-compact">
            <div class="form-group">
              <label class="label-compact">Coût travaux (€ TTC)</label>
              <input
                type="number"
                v-model.number="draft.coutTravauxGPR"
                placeholder="0.00"
                step="0.01"
                min="0"
                class="input-field"
              />
            </div>

            <div class="form-group">
              <label class="label-compact">Coût DAAT (€ TTC)</label>
              <input
                type="number"
                v-model.number="draft.coutDAATGPR"
                placeholder="0.00"
                step="0.01"
                min="0"
                class="input-field"
              />
            </div>

            <div class="form-group">
              <label class="label-compact">Coût Diag Elec (€ TTC)</label>
              <input
                type="number"
                v-model.number="draft.coutDiagElecGPR"
                placeholder="0.00"
                step="0.01"
                min="0"
                class="input-field"
              />
            </div>
          </div>
        </div>

        <!-- Section Sinistre : affichée seulement si une préco est sélectionnée -->
        <div v-if="draft.precoTravauxGPR" class="modal-section sinistre-section">
          <div class="section-title">Déclaration de sinistre</div>
          <div class="sinistre-row">
            <label class="toggle-label">
              <span class="toggle-text">Sinistre</span>
              <div class="toggle-wrapper">
                <input
                  type="checkbox"
                  :checked="draft.sinistre === 'oui'"
                  @change="draft.sinistre = $event.target.checked ? 'oui' : 'non'"
                  class="toggle-input"
                />
                <div class="toggle-slider"></div>
              </div>
            </label>

            <div v-if="draft.sinistre === 'oui'" class="sinistre-comment">
              <label class="label-compact">Commentaires</label>
              <textarea
                v-model="draft.commentairesSinistre"
                placeholder="Commentaires sur le sinistre..."
                rows="2"
                class="input-field textarea-field"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Barre de photos : visible seulement si une préco est sélectionnée -->
        <div v-if="draft.precoTravauxGPR" class="modal-section">
          <div class="section-title">Photos</div>
          <div class="photo-bar">
            <div class="photo-thumbnails">
              <img
                v-for="(photo, pIdx) in (draft.photos || [])"
                :key="pIdx"
                :src="getPhotoSrc(photo)"
                class="photo-thumb"
                @click="openPhotoModal(getPhotoSrc(photo))"
                alt="Photo"
              />
            </div>
            <label for="cameraInputModalGpr" class="photo-add-btn" title="Prendre une photo">
              <i class="fas fa-camera"></i>
            </label>
            <input
              id="cameraInputModalGpr"
              type="file"
              accept="image/*"
              :capture="isAdminUser ? undefined : 'environment'"
              style="display:none"
              @change="addPhoto"
            />
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn-cancel" @click="onCancel">
          <i class="fas fa-times"></i> Annuler
        </button>
        <button type="button" class="btn-confirm" @click="onConfirm">
          <i class="fas fa-check"></i> Confirmer
        </button>
      </div>
    </div>

    <!-- Modal photo preview -->
    <div v-if="photoModal" class="photo-modal-overlay" @click.self="closePhotoModal">
      <div class="photo-modal-content">
        <button type="button" class="photo-modal-close" @click="closePhotoModal">
          <i class="fas fa-times"></i>
        </button>
        <img :src="photoModal" alt="Photo" class="photo-modal-img" />
      </div>
    </div>
  </div>
</template>

<script>
import { getAvailableValuesForField } from '@/utils/preChiffrageHelpers'

export default {
  name: 'ModalGPR',

  props: {
    line: {
      type: Object,
      default: null
    },
    isEditing: {
      type: Boolean,
      default: false
    }
  },

  emits: ['confirm', 'cancel'],

  data() {
    return {
      draft: this.createDraft(),
      precoTravauxOptions: {},
      photoModal: null
    }
  },

  computed: {
    isAdminUser() {
      const user = this.$store?.state?.login?.user || this.$store?.getters?.['user/currentUser']
      return !!(user && user.username === 'boris.parejo')
    },

    sortedPrecoTravauxOptions() {
      return Object.entries(this.precoTravauxOptions)
        .sort(([, a], [, b]) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }))
    }
  },

  methods: {
    createDraft() {
      if (this.line) {
        return JSON.parse(JSON.stringify(this.line))
      }
      return {
        precoTravauxGPR: '',
        dureePrevTravauxGPR: null,
        coutTravauxGPR: null,
        coutDAATGPR: null,
        coutDiagElecGPR: null,
        travauxGPRValidesEDF: '',
        sinistre: '',
        commentairesSinistre: '',
        photos: []
      }
    },

    getPhotoSrc(photo) {
      if (!photo) return ''
      if (typeof photo === 'string') {
        if (photo.startsWith('data:') || photo.startsWith('http')) return photo
        const apiUrl = (window.AppConfig && window.AppConfig.API_URL) ? window.AppConfig.API_URL : ''
        return `${apiUrl}/uploads/prechiffrage/line/${photo}`
      }
      if (photo.src) return photo.src
      return ''
    },

    addPhoto(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        this.draft.photos = [{ src: e.target.result }, ...(this.draft.photos || [])]
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },

    openPhotoModal(src) {
      this.photoModal = src
    },

    closePhotoModal() {
      this.photoModal = null
    },

    onConfirm() {
      this.$emit('confirm', JSON.parse(JSON.stringify(this.draft)))
    },

    onCancel() {
      this.$emit('cancel')
    },

    async loadPrecoTravauxOptions() {
      try {
        const options = await getAvailableValuesForField('preco-travaux-gpr')
        this.precoTravauxOptions = options || {}
      } catch (error) {
        console.error('Erreur lors du chargement des options préco travaux GPR:', error)
        this.precoTravauxOptions = {}
      }
    }
  },

  async mounted() {
    await this.loadPrecoTravauxOptions()
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: min(960px, calc(100vw - 2rem));
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #dbeafe 0%, #f0f7ff 100%);
  flex-shrink: 0;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: $darkblue;
}

.modal-title-badge {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  background: #bfdbfe;
  color: #1e40af;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
}

.modal-close-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #fee2e2;
    color: #dc2626;
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-section {
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 0.65rem;
}

.form-row-compact {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.label-compact {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #374151;
}

.required-mark {
  color: #ef4444;
  margin-left: 0.2rem;
}

.input-field {
  padding: 0.45rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  background: #fff;
  height: 2.4rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
}

.textarea-field {
  height: auto !important;
  resize: vertical;
  min-height: 70px;
}

/* Sinistre */
.sinistre-section {
  background: #fffbf0;
  border-color: #fcd34d;
}

.sinistre-row {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.sinistre-comment {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  user-select: none;
}

.toggle-text {
  font-weight: 600;
  font-size: 0.85rem;
  color: #374151;
}

.toggle-wrapper {
  position: relative;
  width: 2.5rem;
  height: 1.4rem;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + .toggle-slider {
    background-color: #3b82f6;
    &::before { transform: translateX(1.1rem); }
  }
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background-color: #d1d5db;
  border-radius: 1.4rem;
  transition: background-color 0.2s;

  &::before {
    content: '';
    position: absolute;
    height: 1rem;
    width: 1rem;
    left: 0.2rem;
    top: 0.2rem;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }
}

/* Photos */
.photo-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.photo-thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  flex: 1;
}

.photo-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: border-color 0.15s, opacity 0.15s;

  &:hover {
    border-color: #3b82f6;
    opacity: 0.9;
  }
}

.photo-add-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.2s, transform 0.15s;

  &:hover {
    background: #2563eb;
    transform: scale(1.07);
  }
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
}

.btn-cancel {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.1rem;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
}

.btn-confirm {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.25rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
}

/* Photo preview modal */
.photo-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-modal-content {
  position: relative;
  max-width: 92vw;
  max-height: 90vh;
}

.photo-modal-close {
  position: absolute;
  top: -2.2rem;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;

  &:hover { color: #fca5a5; }
}

.photo-modal-img {
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 8px;
  display: block;
}
</style>
